import { Component, OnInit, ViewChild, Input, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  BreadcrumbService,
  ModalComponent,
  UsersService,
  Client,
  SubscriptionsService,
  Group,
  StoreService,
  ProfileService,
  JobsService,
  ClientsService,
  ToastAlertService,
  CsvImportComponent
} from '../../core';
import { StudentsQuestionsService } from '../students-questions.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'lms-students',
  templateUrl: './students.component.html',
  styleUrls: ['./students.component.scss']
})
export class StudentsComponent implements OnInit, OnDestroy {
  @Input() listId: string = 'STUDENTS_LIST';
  @Input() options: any;
  @Input() showActions: boolean;
  @Input() enrollmentGroup: Group;
  @Input() hideTitle: boolean = false;
  public listSubscription: Subscription;

  public client: Client;
  public noStudents: boolean;
  public canAddStudents: boolean;
  public canInvite: boolean;
  public questions: any;
  public isEmailChangeCsv: any;
  @ViewChild('modalCsvUpload', { static: false }) public modalCsvUpload: ModalComponent;
  @ViewChild('modalCsvSelectClient', { static: false }) public modalCsvSelectClient: ModalComponent;
  @ViewChild('downloadcCsvExample', { static: false }) public downloadCsvExample: CsvImportComponent;

  public toolbarConfig: any;

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public breadcrumb: BreadcrumbService,
    public profileService: ProfileService,
    public titleService: Title,
    public translate: TranslateService,
    public usersService: UsersService,
    public jobsService: JobsService,
    public clientsService: ClientsService,
    private studentsQuestionsService: StudentsQuestionsService,
    public subscriptionsService: SubscriptionsService,
    public translateService: TranslateService,
    public toastService: ToastAlertService
  ) {}

  ngOnInit() {
    this.toolbarConfig = {
      list: {
        listId: this.listId,
        download: true,
        filterRow: true,
        columnChooser: false,
        skeletor: true
      }
    };
    this.listSubscription = StoreService.get(this.listId).subscribe((value) => {
      if (value) {
        const hasStudents = value.docs.length > 0;
        const hasSearch = !!value.search;
        const hasFilters = value.filters && Object.keys(value.filters).filter((key) => key !== 'clientId').length > 0;
        this.noStudents = !hasStudents && !hasSearch && !hasFilters;
      }
    });

    if (!this.hideTitle) {
      this.titleService.setTitle(
        this.translate.instant('pageTitle.students') + ' - ' + this.translate.instant('pageTitle.gamelearn')
      );
    }

    this.activatedRoute.data.subscribe((data) => {
      this.client = data['client'];
      this.questions = this.studentsQuestionsService.getImportStudentsQuestions(this.client);
      if (!this.client) {
        this.canAddStudents = true;
        this.canInvite = this.profileService.canExecute('students.invite');
      } else if (this.client.scorm.enabled) {
        this.canAddStudents = false;
        this.canInvite = false;
      } else {
        this.checkSubscription();
      }
    });
  }

  ngOnDestroy(): void {
    this.listSubscription.unsubscribe();
  }

  /**
   * Check if the subscription of the client is active (credits and subscription type)
   * @private
   */
  private checkSubscription(): void {
    if (this.client.subscriptions.users) {
      this.subscriptionsService.getClientSubscription(this.client.id).then((subscription) => {
        if (!subscription) {
          this.canAddStudents = false;
        } else {
          const seatsAvailable: number = subscription.rateLimit - subscription.rateConsumed;
          this.canAddStudents = !!seatsAvailable && new Date(subscription.endDate).getTime() - Date.now() > 0;
          this.canInvite = this.canAddStudents && this.profileService.canExecute('students.invite');
        }
      });
    } else {
      // Credits client
      this.subscriptionsService.getClientCreditsResume(this.client.id).then((credits) => {
        this.canAddStudents = !!credits.available; // Credits client
      });
      this.canInvite = false;
    }
  }

  openCsvModal(isEmailChangeCsv: boolean = false) {
    this.isEmailChangeCsv = isEmailChangeCsv;
    this.client ? this.modalCsvUpload.open() : this.openCsvModalCorporate();
  }

  openCsvModalCorporate() {
    this.modalCsvSelectClient.open();
  }

  closeCsvModalCorporate() {
    this.modalCsvSelectClient.close();
  }

  closeCsvModal() {
    this.isEmailChangeCsv = false;
    this.modalCsvUpload.close();
  }

  onSaveCsv(csvData: any) {
    this.isEmailChangeCsv ? this.changeEmail(csvData) : this.importUsers(csvData);
  }

  /**
   * Example of users import:
   * Email, Nombre, Apellido, Teléfono, DNI, tag1, tag2,  tag3, tag4, tag5
   * max@power.com, Max, Power, 2117029922, 012345A, tag1, tag2,  tag,,
   * daisy.powell83@pharmacon.com, Daisy, Powell, 2887907284, 012345B, tag1, tag5,,,
   * @param csvData
   */
  importUsers(csvData: any) {
    this.usersService
      .importCsvUsers(this.client.id, csvData)
      .then((res) => {
        this.jobsService.manageJob(res.jobId, this.jobsService.jobTypes.CREATE_USERS_BATCH);
      })
      .catch(() => {
        this.showToastAlert();
      });
  }

  changeEmail(csvData: any) {
    this.usersService
      .changeEmailUsers(this.client.id, csvData)
      .then((res) => {
        this.jobsService.manageJob(res.jobId, this.jobsService.jobTypes.CHANGE_USER_EMAIL_BATCH);
      })
      .catch(() => {
        this.showToastAlert();
      });
  }

  showToastAlert() {
    this.toastService
      .showConfirm({
        type: 'toast',
        title: this.translateService.instant('nouns.csvErrorFileTitle'),
        text: this.translateService.instant('nouns.csvErrorFileMessage'),
        actionButtonText: this.translateService.instant('nouns.download')
      })
      .then(() => this.downloadCsvExample.downloadCsv());
  }

  setClient(data: any) {
    if (data.clientId) {
      this.clientsService.getClient(data.clientId).subscribe((client) => {
        this.client = client;
        this.closeCsvModalCorporate();
        this.modalCsvUpload.open();
      });
    }
  }
}
